import { supabase } from './supabase';

interface Experience {
  state: number[];
  action: string;
  reward: number;
  next_state: number[];
  timestamp?: string;
}

interface Feedback {
  action_id: string;
  reward: number;
  context?: Record<string, any>;
}

export const rlClient = {
  async addExperience(experience: Experience) {
    try {
      const { data, error } = await supabase
        .from('rl_experiences')
        .insert({
          state: experience.state,
          action: experience.action,
          reward: experience.reward,
          next_state: experience.next_state,
          timestamp: new Date().toISOString()
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding experience:', error);
      // Store in local storage as fallback
      const experiences = JSON.parse(localStorage.getItem('rlExperiences') || '[]');
      experiences.push(experience);
      localStorage.setItem('rlExperiences', JSON.stringify(experiences));
      throw error;
    }
  },

  async submitFeedback(feedback: Feedback) {
    try {
      const { data, error } = await supabase
        .from('rl_feedback')
        .insert({
          action_id: feedback.action_id,
          reward: feedback.reward,
          context: feedback.context,
          timestamp: new Date().toISOString()
        });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // Store in local storage as fallback
      const feedbacks = JSON.parse(localStorage.getItem('rlFeedback') || '[]');
      feedbacks.push(feedback);
      localStorage.setItem('rlFeedback', JSON.stringify(feedbacks));
      throw error;
    }
  },

  async getModelStats() {
    try {
      const { data: experiences, error: expError } = await supabase
        .from('rl_experiences')
        .select('count');

      const { data: feedback, error: fbError } = await supabase
        .from('rl_feedback')
        .select('count');

      if (expError || fbError) throw expError || fbError;

      return {
        experiences: experiences?.[0]?.count || 0,
        feedback: feedback?.[0]?.count || 0
      };
    } catch (error) {
      console.error('Error getting model stats:', error);
      // Return local stats as fallback
      const experiences = JSON.parse(localStorage.getItem('rlExperiences') || '[]');
      const feedbacks = JSON.parse(localStorage.getItem('rlFeedback') || '[]');
      return {
        experiences: experiences.length,
        feedback: feedbacks.length
      };
    }
  },

  async syncLocalData() {
    try {
      // Sync experiences
      const experiences = JSON.parse(localStorage.getItem('rlExperiences') || '[]');
      if (experiences.length > 0) {
        const { error: expError } = await supabase
          .from('rl_experiences')
          .insert(experiences);
        
        if (!expError) {
          localStorage.removeItem('rlExperiences');
        }
      }

      // Sync feedback
      const feedbacks = JSON.parse(localStorage.getItem('rlFeedback') || '[]');
      if (feedbacks.length > 0) {
        const { error: fbError } = await supabase
          .from('rl_feedback')
          .insert(feedbacks);
        
        if (!fbError) {
          localStorage.removeItem('rlFeedback');
        }
      }
    } catch (error) {
      console.error('Error syncing local data:', error);
    }
  }
};